import React from 'react';
import { COLORS } from '../../../core/constants/colors';
import { EnrichmentSession } from '../types';

interface ExistingSessionConflictModalProps {
  conflict: {
    sessionId: string;
    session: EnrichmentSession | null;
    loading: boolean;
  };
  onView: () => void;
  onSkip: () => void;
  onCancel: () => void;
  onClose: () => void;
  isProcessing: boolean;
}

export const ExistingSessionConflictModal: React.FC<ExistingSessionConflictModalProps> = ({
  conflict,
  onView,
  onSkip,
  onCancel,
  onClose,
  isProcessing,
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: COLORS.extended.orange + '20' }}>
                <svg className="w-6 h-6" style={{ color: COLORS.extended.orange }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1" style={{ color: COLORS.primary.persianIndigo }}>
                  Existing Enrichment Session Found
                </h3>
                <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                  An enrichment session is already in progress for this company
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-gray-100"
            >
              <svg className="w-5 h-5" style={{ color: COLORS.primary.persianIndigo }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {conflict.loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mb-4" style={{ borderColor: COLORS.extended.azure }}></div>
              <p className="text-sm" style={{ color: COLORS.primary.persianIndigo, opacity: 0.7 }}>
                Loading session details...
              </p>
            </div>
          ) : conflict.session ? (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                      Current Score
                    </p>
                    <p className="text-2xl font-bold" style={{ color: COLORS.extended.azure }}>
                      {Math.round(conflict.session.current_score)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                      Progress
                    </p>
                    <p className="text-2xl font-bold" style={{ color: COLORS.primary.softLime }}>
                      {Math.round(conflict.session.progress_percent)}%
                    </p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${conflict.session.progress_percent}%`,
                      backgroundColor: COLORS.extended.azure,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs font-semibold" style={{ color: COLORS.primary.persianIndigo, opacity: 0.6 }}>
                    Session Status
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-1 rounded"
                    style={{
                      backgroundColor: conflict.session.status === 'completed' 
                        ? COLORS.primary.softLime + '20' 
                        : COLORS.extended.orange + '20',
                      color: conflict.session.status === 'completed' 
                        ? COLORS.primary.softLime 
                        : COLORS.extended.orange,
                    }}
                  >
                    {conflict.session.status.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={onView}
                  className="px-4 py-3 rounded-lg font-semibold text-white transition-all"
                  style={{ backgroundColor: COLORS.extended.azure }}
                >
                  View Session
                </button>
                <button
                  onClick={onSkip}
                  disabled={isProcessing}
                  className="px-4 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2"
                  style={{ 
                    borderColor: COLORS.extended.orange,
                    color: COLORS.extended.orange,
                    backgroundColor: 'white',
                  }}
                >
                  {isProcessing ? 'Skipping...' : 'Skip Session'}
                </button>
                <button
                  onClick={onCancel}
                  disabled={isProcessing}
                  className="px-4 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2"
                  style={{ 
                    borderColor: COLORS.extended.redSalsa,
                    color: COLORS.extended.redSalsa,
                    backgroundColor: 'white',
                  }}
                >
                  {isProcessing ? 'Cancelling...' : 'Cancel Session'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-red-50 rounded-lg p-4 border-2 border-red-200">
                <p className="text-sm font-medium" style={{ color: COLORS.extended.redSalsa }}>
                  {conflict.sessionId === 'unknown' 
                    ? 'Session ID could not be determined. Please contact support or try refreshing the page.'
                    : 'Session details could not be loaded. You can try to cancel it below.'}
                </p>
              </div>
              {conflict.sessionId !== 'unknown' && (
                <button
                  onClick={onCancel}
                  disabled={isProcessing}
                  className="w-full px-4 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed border-2"
                  style={{ 
                    borderColor: COLORS.extended.redSalsa,
                    color: COLORS.extended.redSalsa,
                    backgroundColor: 'white',
                  }}
                >
                  {isProcessing ? 'Cancelling...' : 'Cancel Session'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

